/**
 * Onboarding chat hook — drives the SSE conversation with Inea.
 *
 * Streams Inea's reply token by token (fetch + ReadableStream parsing of the
 * text/event-stream body) for a live "Inea is typing" experience.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import i18n from '@/i18n';
import { getToken, apiRequest } from '@/lib/api';

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:4007';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface SessionResponse {
  messages: ChatMessage[];
  status: string;
}

export function useOnboardingChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);
  const initialized = useRef(false);

  // Load existing session (resume mid-conversation).
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    apiRequest<SessionResponse>('/onboarding/session')
      .then((s) => {
        setMessages(s.messages);
        setCompleted(s.status === 'completed');
      })
      .catch(() => setMessages([]))
      .finally(() => setLoaded(true));
  }, []);

  const send = useCallback(
    async (text: string): Promise<void> => {
      if (streaming || completed) return;
      setError('');
      setStreaming(true);

      // Optimistic: show the user's message + an empty assistant bubble.
      setMessages((prev) => [
        ...prev,
        { role: 'user', content: text },
        { role: 'assistant', content: '' },
      ]);

      try {
        const res = await fetch(`${API_BASE}/onboarding/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken() ?? ''}`,
            // L'entretien se déroule dans la langue du membre : ce flux SSE
            // ne passe pas par apiRequest, il doit porter l'en-tête lui-même.
            'X-Lang': (localStorage.getItem('nia-lang') ?? 'fr').slice(0, 2),
          },
          body: JSON.stringify({ message: text }),
        });

        if (!res.ok || !res.body) {
          const data: unknown = await res.json().catch(() => ({}));
          const msg = (data as { message?: string }).message ?? i18n.t('common.networkError');
          throw new Error(msg);
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        // Parse the SSE stream incrementally.
        const processEvent = (evt: string): void => {
          const line = evt.trim();
          if (!line.startsWith('data: ')) return;
          const payload = JSON.parse(line.slice(6)) as
            | { type: 'token'; text: string }
            | { type: 'done'; complete: boolean }
            | { type: 'error'; message: string };

          if (payload.type === 'token') {
            setMessages((prev) => {
              const next = [...prev];
              const last = next[next.length - 1];
              if (last && last.role === 'assistant') {
                next[next.length - 1] = { ...last, content: last.content + payload.text };
              }
              return next;
            });
          } else if (payload.type === 'done') {
            setCompleted(payload.complete);
          } else {
            throw new Error(payload.message);
          }
        };

        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split('\n\n');
          buffer = events.pop() ?? '';
          for (const evt of events) processEvent(evt);
        }
        // Flush any trailing event left in the buffer (no trailing \n\n).
        if (buffer.trim()) processEvent(buffer);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur pendant la conversation.');
        // Roll back the empty assistant bubble on hard failure.
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last && last.role === 'assistant' && last.content === '') {
            return prev.slice(0, -1);
          }
          return prev;
        });
      } finally {
        setStreaming(false);
      }
    },
    [streaming, completed],
  );

  return { messages, send, streaming, completed, error, loaded };
}
